import {
  ArrowRight,
  Check,
  GraduationCap,
  Pause,
  Play,
  UsersRound
} from "lucide-react";
import type { CSSProperties } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { BrandLogo } from "../components/BrandLogo";
import { gameDefinitions } from "../games/catalog";
import "./AboutPage.css";

const languages = [
  {
    name: "Spanish",
    copy: "Build Spanish classes with verb sets, vocabulary and clear goals.",
    tags: ["Classes", "Verb sets", "Vocabulary sets"],
    avatar: "/images/recraft-pop/avatars/nia-pop-v1.svg",
    tone: "yellow",
    flag: "spain"
  },
  {
    name: "French",
    copy: "Plan French practice with the same simple class flow.",
    tags: ["Weekly plan", "Games", "Review"],
    avatar: "/images/recraft-pop/avatars/mateo-pop-v1.svg",
    tone: "pink",
    flag: "france"
  },
  {
    name: "Italian",
    copy: "Keep Italian materials together and track progress.",
    tags: ["Verbs", "Vocabulary", "Progress"],
    avatar: "/images/recraft-pop/avatars/imani-pop-v1.svg",
    tone: "green",
    flag: "italy"
  }
] as const;

const steps = [
  {
    title: "Plan",
    copy: "Create a class and choose the language you want to teach."
  },
  {
    title: "Play",
    copy: "Turn verb and vocabulary goals into games students want to play."
  },
  {
    title: "Review",
    copy: "See completed work, points and progress in one clear place."
  }
] as const;

const allGames = Object.values(gameDefinitions);
const playableGameCount = allGames.filter((game) => game.playable).length;
const gameTones = ["pink", "green", "yellow", "blue"] as const;

function gameTone(index: number) {
  return gameTones[index % gameTones.length];
}

function HeroArtwork() {
  return (
    <picture className="about-masthead__artwork">
      <source media="(max-width: 720px)" srcSet="/images/recraft-pop/about-hero-art-mobile-v1.png" />
      <img src="/images/recraft-pop/about-hero-art-desktop-v1.png" alt="" width="1200" height="1800" fetchPriority="high" />
    </picture>
  );
}

const roleCards = [
  {
    role: "Teacher",
    title: "Plan the class",
    icon: GraduationCap,
    tone: "yellow",
    items: ["Manage students and notes", "Build the weekly plan", "Inspect class progress"]
  },
  {
    role: "Student",
    title: "Work through goals",
    icon: UsersRound,
    tone: "blue",
    items: ["See active class goals", "Open available games", "Review goals, attempts and points"]
  }
] as const;

const comparisonBrands = [
  {
    name: "Duolingo",
    mark: "D",
    tone: "green",
    logo: "https://cdn.simpleicons.org/duolingo/58CC02",
    solves: "Daily language habits",
    adds: "Teacher goals + class context"
  },
  {
    name: "Memrise",
    mark: "M",
    tone: "yellow",
    logo: "https://www.memrise.com/favicon.ico",
    solves: "Video + vocabulary",
    adds: "Teacher goals + class context"
  },
  {
    name: "Babbel",
    mark: "B",
    tone: "pink",
    logo: "https://www.babbel.com/favicon.svg",
    solves: "Lessons + speaking",
    adds: "Chosen verbs + class games"
  },
  {
    name: "Busuu",
    mark: "B",
    tone: "green",
    logo: "https://busuu.com/favicon.ico",
    solves: "Courses + community",
    adds: "Daily goals + teacher reports"
  },
  {
    name: "Mondly",
    mark: "M",
    tone: "blue",
    logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Mondly_Official_Logo.svg",
    solves: "Fast multilingual lessons",
    adds: "Conjugations + class goals"
  },
  {
    name: "Drops",
    mark: "D",
    tone: "coral",
    logo: "https://www.google.com/s2/favicons?domain=languagedrops.com&sz=128",
    solves: "Visual vocabulary drills",
    adds: "Grammar + sentences"
  },
  {
    name: "Clozemaster",
    mark: "C",
    tone: "purple",
    logo: "https://www.google.com/s2/favicons?domain=clozemaster.com&sz=128",
    solves: "Cloze + spaced repetition",
    adds: "Teacher-led practice + review"
  }
] as const;

const comparisonSessionThemes = [
  { id: "lilac", a: "#5b36d9", b: "#7a45e8", c: "#4b2ab8" },
  { id: "cobalt", a: "#3151c9", b: "#4867e0", c: "#233aa8" },
  { id: "berry", a: "#b72d73", b: "#d2478e", c: "#922157" },
  { id: "teal", a: "#087f82", b: "#0b9b92", c: "#06666e" }
] as const;

type ComparisonSessionTheme = typeof comparisonSessionThemes[number];

function getComparisonSessionTheme(): ComparisonSessionTheme {
  const fallback = comparisonSessionThemes[0];

  try {
    const savedId = window.sessionStorage.getItem("verbcoach-comparison-session-theme");
    const savedTheme = comparisonSessionThemes.find((theme) => theme.id === savedId);
    if (savedTheme) return savedTheme;

    const theme = comparisonSessionThemes[Math.floor(Math.random() * comparisonSessionThemes.length)] ?? fallback;
    window.sessionStorage.setItem("verbcoach-comparison-session-theme", theme.id);
    return theme;
  } catch {
    return fallback;
  }
}

export function AboutPage() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [motionPaused, setMotionPaused] = useState(false);
  const [scrollSection, setScrollSection] = useState("masthead");
  const [comparisonSessionTheme] = useState(getComparisonSessionTheme);
  const comparisonSessionStyle = {
    "--comparison-session-a": comparisonSessionTheme.a,
    "--comparison-session-b": comparisonSessionTheme.b,
    "--comparison-session-c": comparisonSessionTheme.c
  } as CSSProperties;

  useEffect(() => {
    let active = true;
    let resetMotion: (() => void) | undefined;

    void import("gsap")
      .then(({ default: gsap }) => {
        if (!active) return;
        gsap.globalTimeline.paused(motionPaused);
        resetMotion = () => { gsap.globalTimeline.paused(false); };
      })
      .catch(() => undefined);

    return () => {
      active = false;
      resetMotion?.();
    };
  }, [motionPaused]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const previousTitle = document.title;
    document.title = "verbcoach";
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.content;
    if (description) {
      description.content = "Create language classes, assign verb and vocabulary goals, play activities and review progress.";
    }

    let disposed = false;
    let context: { revert: () => void } | undefined;
    const cleanups: Array<() => void> = [];

    const scrollSections = Array.from(root.querySelectorAll<HTMLElement>("[data-scroll-section]"));
    if (scrollSections.length && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        const visibleSection = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => second.intersectionRatio - first.intersectionRatio)[0]
          ?.target as HTMLElement | undefined;
        const nextSection = visibleSection?.dataset.scrollSection;
        if (nextSection) setScrollSection(nextSection);
      }, { rootMargin: "-18% 0px -54% 0px", threshold: [0, .2, .45, .7, 1] });

      scrollSections.forEach((section) => observer.observe(section));
      cleanups.push(() => observer.disconnect());
    }

    void Promise.all([import("gsap"), import("gsap/ScrollTrigger")]).then(([gsapModule, scrollTriggerModule]) => {
      if (disposed) return;
      const gsap = gsapModule.default;
      const { ScrollTrigger } = scrollTriggerModule;
      gsap.registerPlugin(ScrollTrigger);
      context = gsap.context(() => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (reduceMotion) {
        gsap.set("[data-hero-in], [data-reveal], [data-language], [data-game], [data-role]", { clearProps: "all", opacity: 1 });
        return;
      }

      const intro = gsap.timeline({ defaults: { ease: "power3.out" } });
      intro
        .from(".about-header", { y: -18, opacity: 0, duration: 0.4 })
        .from(".about-masthead__word", { yPercent: 105, rotate: 2, duration: 0.5, stagger: 0.045 }, "<0.03")
        .from(".about-masthead__lead", { y: 18, opacity: 0, duration: 0.42 }, "<0.16")
        .from(".hero-launch", { y: 22, opacity: 0, scale: 0.94, duration: 0.58, ease: "back.out(1.7)" }, "<0.08")
        .from(".hero-launch__arrow", { x: -16, rotate: -12, duration: 0.42, ease: "back.out(2)" }, "<0.18")
        .from(".about-masthead__artwork", { x: 55, opacity: 0, duration: 0.58 }, "<0.04");

      gsap.utils.toArray<HTMLElement>("[data-reveal]").forEach((element) => {
        gsap.from(element, {
          y: 28,
          opacity: 0,
          duration: 0.48,
          ease: "power3.out",
          scrollTrigger: { trigger: element, start: "top 88%", toggleActions: "play reverse play reverse" }
        });
      });

      gsap.from("[data-language]:not([data-language-featured])", {
        y: 30,
        opacity: 0,
        duration: 0.48,
        stagger: 0.07,
        ease: "power3.out",
        scrollTrigger: { trigger: ".about-languages__grid", start: "top 82%", toggleActions: "play reverse play reverse" }
      });

      gsap.from(".about-step", {
        y: 24,
        opacity: 0,
        duration: 0.5,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: { trigger: ".about-steps__grid", start: "top 82%", toggleActions: "play reverse play reverse" }
      });

      gsap.from(".about-support__role", {
        y: 22,
        opacity: 0,
        duration: 0.46,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: { trigger: ".about-support__promise", start: "top 82%", toggleActions: "play reverse play reverse" }
      });
        gsap.from(".about-support__comparison-table__row", {
          y: 22,
          opacity: 0,
          duration: 0.34,
          stagger: 0.045,
          ease: "power2.out",
          scrollTrigger: { trigger: ".about-support__comparison-table", start: "top 86%", toggleActions: "play reverse play reverse" }
      });

      gsap.utils.toArray<HTMLElement>("[data-game-track]").forEach((track) => {
        const direction = track.dataset.direction === "right" ? 1 : -1;
        const distance = Math.max(0, track.scrollWidth - (track.parentElement?.clientWidth ?? track.scrollWidth));
        if (!distance) return;
        gsap.fromTo(track, { x: direction > 0 ? -Math.min(distance, 180) : 0 }, {
          x: direction > 0 ? 0 : -Math.min(distance, 180),
          ease: "none",
          scrollTrigger: { trigger: ".about-games__tracks", start: "top 82%", end: "bottom 35%", scrub: 0.6 }
        });
      });

      gsap.from("[data-role]", {
        y: 26,
        opacity: 0,
        duration: 0.52,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: { trigger: ".about-roles__grid", start: "top 82%", toggleActions: "play reverse play reverse" }
      });
      gsap.from(".about-roles__bridge", {
        scale: 0.86,
        opacity: 0,
        duration: 0.42,
        ease: "power3.out",
        scrollTrigger: { trigger: ".about-roles__grid", start: "top 76%", toggleActions: "play reverse play reverse" }
      });
      gsap.from(".about-role__list li", {
        x: -12,
        opacity: 0,
        duration: 0.38,
        stagger: 0.05,
        ease: "power2.out",
        scrollTrigger: { trigger: ".about-roles__grid", start: "top 72%", toggleActions: "play reverse play reverse" }
      });
      gsap.from(".about-final__faces img", {
        y: 26,
        opacity: 0,
        duration: 0.48,
        stagger: { each: 0.06, from: "center" },
        ease: "power3.out",
        scrollTrigger: { trigger: ".about-final", start: "top 72%", toggleActions: "play reverse play reverse" }
      });

      const progress = root.querySelector<HTMLElement>(".about-header__progress span");
      if (progress) {
        const setProgress = gsap.quickSetter(progress, "scaleX");
        ScrollTrigger.create({
          trigger: root,
          start: "top top",
          end: "bottom bottom",
          onUpdate: (self) => setProgress(self.progress)
        });
      }

      }, root);
    }).catch(() => {
      context?.revert();
      root.querySelectorAll<HTMLElement>("[style]").forEach((element) => element.removeAttribute("style"));
    });

    return () => {
      disposed = true;
      cleanups.forEach((cleanup) => cleanup());
      context?.revert();
      document.title = previousTitle;
      if (description && previousDescription !== undefined) description.content = previousDescription;
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={`about-page${motionPaused ? " is-motion-paused" : ""}`}
      data-comparison-session-theme={comparisonSessionTheme.id}
      data-scroll-section={scrollSection}
      style={comparisonSessionStyle}
    >
      <header className="about-header">
        <a className="about-header__brand" href="#top" aria-label="VerbCoach home">
          <BrandLogo alt="VerbCoach" />
        </a>
        <nav className="about-header__nav" aria-label="About page">
          <a href="#workflow">Workflow</a>
          <a href="#languages">Languages</a>
          <a href="#games">Games</a>
          <a href="#progress">Progress</a>
        </nav>
        <div className="about-header__actions">
          <button className="about-motion-toggle" type="button" onClick={() => setMotionPaused((paused) => !paused)} aria-pressed={motionPaused} title={motionPaused ? "Play page motion" : "Pause page motion"}>
            {motionPaused ? <Play aria-hidden="true" size={17} /> : <Pause aria-hidden="true" size={17} />}
            <span>{motionPaused ? "Play motion" : "Pause motion"}</span>
          </button>
          <a className="about-button about-button--ink about-header__login" href="/login">Log in</a>
        </div>
        <span className="about-header__progress" aria-hidden="true"><span /></span>
      </header>

      <main>
        <section id="top" data-scroll-section="masthead" className="about-masthead">
          <HeroArtwork />
          <div className="about-masthead__inner">
            <div className="about-masthead__copy">
              <h1>
                <span className="about-masthead__line"><span className="about-masthead__word">Plan.</span></span>
                <span className="about-masthead__line about-masthead__line--pink"><span className="about-masthead__word">Play.</span></span>
                <span className="about-masthead__line"><span className="about-masthead__word">Progress.</span></span>
              </h1>
              <p className="about-masthead__lead">Teachers turn verb and vocabulary sets into class goals. Students practise them through games, earn points and return to completed work in Review.</p>
              <div className="about-masthead__actions">
                <a
                  className="hero-launch"
                  href="/login"
                  title="Open VerbCoach"
                  aria-label="Open VerbCoach"
                >
                  <span className="hero-launch__copy">
                    <span className="hero-launch__label">Open VerbCoach</span>
                  </span>
                  <span className="hero-launch__arrow" aria-hidden="true"><ArrowRight size={28} /></span>
                </a>
              </div>
            </div>

            <div className="about-masthead__stage" data-hero-stage aria-hidden="true" />
          </div>
        </section>

        <section id="languages" data-scroll-section="languages" className="about-section about-languages">
          <header className="about-section__heading" data-reveal>
            <h2>Spanish, French and Italian. One simple workflow.</h2>
          </header>
          <div className="about-languages__grid">
            {languages.map((language) => (
              <article key={language.name} className={`about-language about-language--${language.tone}`} data-language data-tilt data-language-featured={language.name === "French" ? "true" : undefined}>
                <div className={`about-language__flag about-language__flag--${language.flag}`} aria-hidden="true"><span /><span /><span /></div>
                <div className="about-language__portrait"><img src={language.avatar} alt="" width="420" height="420" loading="lazy" /></div>
                <div className="about-language__copy">
                  <p>Class language</p>
                  <h3>{language.name}</h3>
                  <span>{language.copy}</span>
                  <ul>{language.tags.map((tag) => <li key={tag}>{tag}</li>)}</ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="support" data-scroll-section="support" className="about-section about-support">
          <header className="about-section__heading about-support__heading" data-reveal>
            <h2>Built around the teacher, not instead of them.</h2>
          </header>

          <div className="about-support__promise">
            <div className="about-support__rail" aria-hidden="true">
              <span>One system / two audiences</span>
            </div>
            <div className="about-support__roles">
              <article className="about-support__role about-support__role--teacher">
                <div className="about-support__role-topline">
                  <span>Teacher</span>
                  <span>Plan and respond</span>
                </div>
                <h3>For teachers</h3>
                <p>Turn a goal into practice, see who needs help and keep feedback moving.</p>
              </article>
              <article className="about-support__role about-support__role--student">
                <div className="about-support__role-topline">
                  <span>Student</span>
                  <span>Practice and improve</span>
                </div>
                <h3>For students</h3>
                <p>Get AI guidance inside the games and grow day by day, goal after goal.</p>
              </article>
            </div>
          </div>

          <div className="about-support__comparison" aria-labelledby="comparison-title">
            <div className="about-support__comparison-intro">
              <div>
                <h3 id="comparison-title">What the ecosystem already does well</h3>
              </div>
            </div>
            <div className="about-support__comparison-table" role="table" aria-label="Language learning ecosystem comparison">
              <div className="about-support__comparison-table-head" role="row">
                <span role="columnheader">Platform</span>
                <span role="columnheader">They make easier</span>
                <div className="about-support__comparison-table-logo" role="columnheader">
                  <img src="/images/logo-wordmark.png?v=20260817-animated-wordmark" alt="VerbCoach" width="180" height="40" />
                </div>
              </div>
              {comparisonBrands.map((brand) => (
                <div className={`about-support__comparison-table__row about-support__comparison-table__row--${brand.tone}`} role="row" key={brand.name}>
                  <div className="about-support__comparison-table__brand" role="cell">
                    <span className="about-support__comparison-table__mark"><span aria-hidden="true">{brand.mark}</span><img src={brand.logo} alt="" width="32" height="32" loading="lazy" onError={(event) => { event.currentTarget.hidden = true; }} /></span>
                    <strong>{brand.name}</strong>
                  </div>
                  <span className="about-support__comparison-table__solves" role="cell">{brand.solves}</span>
                  <div className="about-support__comparison-table__adds" role="cell">
                    <span className="about-support__comparison-table__check" aria-hidden="true"><Check size={15} /></span>
                    <strong>{brand.adds}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="workflow" data-scroll-section="workflow" className="about-section about-steps">
          <header className="about-section__heading about-section__heading--compact" data-reveal>
            <h2 aria-label="Goals in. Progress out.">
              Goals in. <span>Progress out.</span>
            </h2>
          </header>
          <div className="about-steps__grid">
            {steps.map((step) => (
              <article className="about-step" key={step.title} data-tilt>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="games" data-scroll-section="games" className="about-section about-games">
          <header className="about-section__heading about-games__heading" data-reveal>
            <h2>
              <span>One class.</span>
              {" "}
              <span>A whole arcade.</span>
            </h2>
            <div
              className="about-games__scoreboard"
              role="group"
              aria-label={`${playableGameCount} games playable now, ${allGames.length} games total`}
            >
              <span className="about-games__score" aria-hidden="true">
                <strong>{playableGameCount}</strong>
                <span>Playable now</span>
              </span>
              <span className="about-games__score" aria-hidden="true">
                <strong>{allGames.length}</strong>
                <span>Games total</span>
              </span>
            </div>
          </header>
          <div className="about-games__tracks" aria-label="All VerbCoach games">
            {[
              { direction: "right", games: allGames },
              { direction: "left", games: [...allGames].reverse() }
            ].map(({ direction, games }) => (
              <div className="about-games__track-viewport" data-track-lane={direction} key={direction} aria-hidden={direction === "left"}>
                <div className="about-games__track" data-game-track data-direction={direction}>
                  {games.map((game, index) => (
                    <article className={`about-game about-game--${gameTone(index)}${game.playable ? " about-game--playable" : ""}`} key={`${direction}-${game.id}`} data-game>
                      <div className="about-game__media"><img src={game.banner ?? game.cover} alt={`${game.name} game preview`} width="1200" height="380" loading="lazy" /></div>
                      <div className="about-game__copy">
                        <div className="about-game__meta"><span>{game.kicker}</span><em>{game.playable ? "Playable now" : "Coming soon"}</em></div>
                        <h3>{game.name}</h3>
                        <p>{game.description}</p>
                        <div className="about-game__footer"><small>{game.format}</small><b>{game.difficulty}</b></div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="progress" data-scroll-section="progress" className="about-section about-roles">
          <header className="about-section__heading about-section__heading--compact" data-reveal>
            <h2>One class. Two roles.</h2>
          </header>
          <div className="about-roles__grid">
            {roleCards.map(({ role, title, icon: Icon, tone, items }) => (
              <div className={`about-role-group about-role-group--${tone}`} key={role} data-role data-tilt>
                <article className={`about-role about-role--${tone}`}>
                  <div className="about-role__head">
                    <span><Icon size={25} /></span>
                    <p>{role}</p>
                  </div>
                  <h3>{title}</h3>
                </article>
                <ul className="about-role__list" aria-label={`${role} features`}>
                  {items.map((item) => <li key={item}><Check size={17} />{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section data-scroll-section="final" className="about-final">
          <div className="about-final__faces" aria-hidden="true">
            {["ada", "kai", "zuri", "sami", "luz"].map((avatar) => <img key={avatar} src={`/images/recraft-pop/avatars/${avatar}-pop-v1.svg`} alt="" width="150" height="150" loading="lazy" />)}
          </div>
          <h2>Keep the material connected from plan to progress.</h2>
          <a className="about-button about-button--yellow" href="/login">Log in to VerbCoach <ArrowRight aria-hidden="true" size={19} /></a>
          <img
            className="about-final__learning-art"
            src="/images/generated/verbcoach-language-learning-art.png"
            alt=""
            width="260"
            height="260"
            aria-hidden="true"
          />
        </section>
      </main>

      <footer data-scroll-section="footer" className="about-footer">
        <BrandLogo alt="VerbCoach" />
        <nav aria-label="Footer"><a href="#workflow">Workflow</a><a href="#languages">Languages</a><a href="#games">Games</a><a href="#progress">Progress</a><a href="/privacy">Privacy</a></nav>
        <p>© 2026 VerbCoach</p>
      </footer>
    </div>
  );
}
